import { findTabbableDescendants } from './tabbable';

/**
 * This function scopes the tabbing within a specific node.
 * It prevents the default tabbing behavior and cycles through tabbable elements within the node.
 *
 * @param {HTMLElement} node - The root element to scope the tabbing within.
 * @param {KeyboardEvent} event - The keyboard event that triggered the tabbing.
 */
export function scopeTab(node: HTMLElement, event: KeyboardEvent) {
  const tabbable = findTabbableDescendants(node);

  if (!tabbable.length) {
    event.preventDefault();
    return;
  }

  const root = (node.getRootNode() as unknown) as DocumentOrShadowRoot;

  // check which element is currently focused
  let currentFocusedElement = root.activeElement as HTMLElement;
  // find index of the currently focused element in tabbable elements
  const currentFocusedIndex = tabbable.indexOf(currentFocusedElement);

  // if currently focused element is not tabbable, set it to the first tabbable element
  if (currentFocusedIndex === -1) {
    currentFocusedElement = tabbable[0] || node;
    currentFocusedElement.focus();
    event.preventDefault();
    return;
  } else {
    // select next tabbable element
    const nextTabbableIndex = event.shiftKey ? currentFocusedIndex - 1 : currentFocusedIndex + 1;
    // if next tabbable element is out of bounds, set it to the first or last tabbable element
    if (nextTabbableIndex < 0 || nextTabbableIndex >= tabbable.length) {
      currentFocusedElement = tabbable[event.shiftKey ? tabbable.length - 1 : 0];
    } else {
      currentFocusedElement = tabbable[nextTabbableIndex];
    }
    currentFocusedElement.focus();
    event.preventDefault();
    return;
  }
}
